/**
 * MedicationRepository — schedules, occurrences, intake events, proof videos.
 *
 * Policy enforcement (mirrored by RLS + SQL triggers as defence in depth):
 * - intake writes (markTaken/undoTaken/attachProof) are client-only: the
 *   occurrence must belong to the session user;
 * - undoTaken has a bounded undo window of 30 minutes (UNDO_WINDOW_MS),
 *   enforced here AND by the `enforce_undo_window` SQL trigger;
 * - schedule creation for another user is only possible through an active
 *   caregiver link (RLS `is_linked_caregiver`);
 * - proof videos live in the private `medication-proof-videos` bucket at
 *   `medication-proof-videos/<client_id>/<occurrence_id>/<uuid>.<ext>`;
 *   playback uses short-lived signed URLs only;
 * - addCaregiverNote writes a distinct audit event (action
 *   'caregiver_note') — it never impersonates a client intake.
 */
import { BUCKETS, requireSupabase, SIGNED_URL_TTL_SECONDS } from '@/lib/supabase/client'
import {
  auditEventFromRow,
  medicationIntakeEventFromRow,
  medicationOccurrenceFromRow,
  medicationScheduleFromRow,
  medicationScheduleToInsert,
  medicationScheduleToPatch,
  type AuditEventRow,
  type MedicationIntakeEventRow,
  type MedicationOccurrenceRow,
  type MedicationScheduleRow,
} from '@/lib/supabase/mappers'
import type { MedicationIntakeEvent, MedicationOccurrence, MedicationSchedule } from '@/types'
import type { MedicationRepository } from '../interfaces'
import { assertRowReturned, fail, newObjectId, notFoundError, requireUserId, validationError } from './helpers'

/** Bounded undo window for markTaken -> undoTaken (spec: 30 minutes). */
export const UNDO_WINDOW_MS = 30 * 60 * 1000

const MAX_PROOF_VIDEO_BYTES = 50 * 1024 * 1024

const PROOF_EXTENSION_BY_MIME: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
}

const ALLOWED_PROOF_EXTENSIONS = new Set(['mp4', 'webm', 'mov'])

function validateProofVideoFile(file: File): { extension: string } {
  const mime = file.type.toLowerCase()
  const nameMatch = /\.([a-z0-9]+)$/i.exec(file.name)
  const nameExtension = nameMatch ? nameMatch[1].toLowerCase() : ''
  const extension =
    PROOF_EXTENSION_BY_MIME[mime] ?? (ALLOWED_PROOF_EXTENSIONS.has(nameExtension) ? nameExtension : '')

  if (!mime.startsWith('video/') && !ALLOWED_PROOF_EXTENSIONS.has(nameExtension)) {
    throw validationError(
      'PROOF_TYPE_INVALID',
      'Dit bestand is geen video. Kies een videobestand.',
      `type=${file.type || 'unknown'}`,
    )
  }
  if (!extension) {
    throw validationError(
      'PROOF_EXTENSION_INVALID',
      'Dit videoformaat wordt niet ondersteund. Gebruik mp4, webm of mov.',
      `type=${file.type || 'unknown'} ext=${nameExtension || 'none'}`,
    )
  }
  if (file.size > MAX_PROOF_VIDEO_BYTES) {
    throw validationError(
      'PROOF_TOO_LARGE',
      'De video is groter dan 50 MB. Kies een kortere video.',
      `size=${file.size}`,
    )
  }
  if (file.size === 0) {
    throw validationError('PROOF_EMPTY', 'Deze video is leeg. Neem een nieuwe video op.')
  }
  return { extension }
}

async function fetchOccurrenceOrThrow(occurrenceId: string): Promise<MedicationOccurrence> {
  const supabase = requireSupabase()
  const { data, error } = await supabase
    .from('medication_occurrences')
    .select('*')
    .eq('id', occurrenceId)
    .maybeSingle()
  if (error) fail(error)
  if (!data) fail(notFoundError('Dit medicatiemoment bestaat niet of je hebt er geen toegang toe.'))
  return medicationOccurrenceFromRow(data as MedicationOccurrenceRow)
}

async function fetchIntakeEventOrThrow(intakeEventId: string): Promise<MedicationIntakeEvent> {
  const supabase = requireSupabase()
  const { data, error } = await supabase
    .from('medication_intake_events')
    .select('*')
    .eq('id', intakeEventId)
    .maybeSingle()
  if (error) fail(error)
  if (!data) fail(notFoundError('Deze inname-registratie bestaat niet of je hebt er geen toegang toe.'))
  return medicationIntakeEventFromRow(data as MedicationIntakeEventRow)
}

async function updateOccurrenceStatus(occurrenceId: string, status: MedicationOccurrence['status']): Promise<void> {
  const supabase = requireSupabase()
  const { error } = await supabase
    .from('medication_occurrences')
    .update({ status })
    .eq('id', occurrenceId)
  if (error) fail(error)
}

/** Best-effort orphan cleanup for replaced proof objects. */
async function removeProofBestEffort(path: string): Promise<void> {
  try {
    const supabase = requireSupabase()
    const { error } = await supabase.storage.from(BUCKETS.medicationProofVideos).remove([path])
    if (error) console.warn('[medication] proof cleanup failed', error.message)
  } catch {
    // never mask a confirmed write
  }
}

export const medicationRepository: MedicationRepository = {
  async listSchedules(clientId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const effectiveClientId = clientId ?? userId
    const { data, error } = await supabase
      .from('medication_schedules')
      .select('*')
      .eq('client_id', effectiveClientId)
      .order('created_at', { ascending: false })
    if (error) fail(error)
    return ((data ?? []) as MedicationScheduleRow[]).map(medicationScheduleFromRow)
  },

  async createSchedule(input): Promise<MedicationSchedule> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    // created_by is always the session user (mapper enforces); client_id may
    // only differ from the session user via an active caregiver link (RLS).
    const { data, error } = await supabase
      .from('medication_schedules')
      .insert(medicationScheduleToInsert(input, userId))
      .select()
      .single()
    return medicationScheduleFromRow(assertRowReturned(data, error) as MedicationScheduleRow)
  },

  async updateSchedule(id, patch) {
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase
      .from('medication_schedules')
      .update(medicationScheduleToPatch(patch))
      .eq('id', id)
      .select()
      .single()
    return medicationScheduleFromRow(assertRowReturned(data, error) as MedicationScheduleRow)
  },

  async deactivateSchedule(id) {
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase
      .from('medication_schedules')
      .update({ active: false })
      .eq('id', id)
      .select()
      .single()
    return medicationScheduleFromRow(assertRowReturned(data, error) as MedicationScheduleRow)
  },

  async listOccurrences(startDate, endDate, clientId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const effectiveClientId = clientId ?? userId
    // Bounded materialization window only — never an unbounded query.
    const { data, error } = await supabase
      .from('medication_occurrences')
      .select('*')
      .eq('client_id', effectiveClientId)
      .gte('local_date', startDate)
      .lte('local_date', endDate)
      .order('scheduled_for', { ascending: true })
    if (error) fail(error)
    return ((data ?? []) as MedicationOccurrenceRow[]).map(medicationOccurrenceFromRow)
  },

  async markTaken(occurrenceId, clientNote) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const occurrence = await fetchOccurrenceOrThrow(occurrenceId)
    // Client-only action: the code-level guard complements the RLS policy
    // (`medication_intake_events_insert_client_only`).
    if (occurrence.clientId !== userId) {
      fail(
        validationError(
          'INTAKE_NOT_OWN',
          'Je kunt alleen je eigen medicatiemomenten registreren.',
          `occurrence.clientId != session user`,
        ),
      )
    }

    const { data, error } = await supabase
      .from('medication_intake_events')
      .insert({
        occurrence_id: occurrenceId,
        client_id: userId,
        taken_at: new Date().toISOString(),
        status: 'taken',
        client_note: clientNote ?? null,
      })
      .select()
      .single()
    const event = medicationIntakeEventFromRow(assertRowReturned(data, error) as MedicationIntakeEventRow)
    await updateOccurrenceStatus(occurrenceId, 'taken')
    return event
  },

  async undoTaken(intakeEventId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const event = await fetchIntakeEventOrThrow(intakeEventId)
    if (event.clientId !== userId) {
      fail(validationError('INTAKE_NOT_OWN', 'Je kunt alleen je eigen inname terugzetten.'))
    }
    if (event.status !== 'taken' || !event.takenAt) {
      fail(
        validationError(
          'UNDO_NOT_ALLOWED',
          'Deze inname kan niet meer worden teruggezet.',
          `status=${event.status}`,
        ),
      )
    }
    // Bounded undo window (30 min), also enforced by the SQL trigger
    // `medication_intake_events_enforce_undo_window`.
    const takenAtMs = Date.parse(event.takenAt)
    if (Number.isNaN(takenAtMs) || Date.now() - takenAtMs > UNDO_WINDOW_MS) {
      fail(
        validationError(
          'UNDO_WINDOW_EXPIRED',
          'De termijn om de inname terug te zetten (30 minuten) is verstreken.',
          `taken_at=${event.takenAt}`,
        ),
      )
    }

    const { data, error } = await supabase
      .from('medication_intake_events')
      .update({ taken_at: null, status: 'planned' })
      .eq('id', intakeEventId)
      .select()
      .single()
    const updated = medicationIntakeEventFromRow(assertRowReturned(data, error) as MedicationIntakeEventRow)
    await updateOccurrenceStatus(event.occurrenceId, 'due')
    return updated
  },

  async attachProof(intakeEventId, file) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const event = await fetchIntakeEventOrThrow(intakeEventId)
    if (event.clientId !== userId) {
      fail(validationError('INTAKE_NOT_OWN', 'Je kunt alleen bij je eigen inname een bewijsvideo uploaden.'))
    }
    const { extension } = validateProofVideoFile(file)

    const objectPath = `${event.clientId}/${event.occurrenceId}/${newObjectId()}.${extension}`
    const { error: uploadError } = await supabase.storage
      .from(BUCKETS.medicationProofVideos)
      .upload(objectPath, file, { contentType: file.type || undefined, upsert: false })
    if (uploadError) fail(uploadError, 'storage')

    const previousPath = event.proofObjectPath
    const { data, error } = await supabase
      .from('medication_intake_events')
      .update({
        proof_object_path: objectPath,
        proof_uploaded_at: new Date().toISOString(),
        caregiver_viewed_at: null,
        status: 'proof_uploaded',
      })
      .eq('id', intakeEventId)
      .select()
      .single()

    if (error || !data) {
      await removeProofBestEffort(objectPath)
      fail(error ?? notFoundError())
    }

    await updateOccurrenceStatus(event.occurrenceId, 'proof_uploaded')
    if (previousPath && previousPath !== objectPath) await removeProofBestEffort(previousPath)
    return medicationIntakeEventFromRow(data as MedicationIntakeEventRow)
  },

  async getProofSignedUrl(intakeEvent) {
    if (!intakeEvent.proofObjectPath) return null
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase.storage
      .from(BUCKETS.medicationProofVideos)
      .createSignedUrl(intakeEvent.proofObjectPath, SIGNED_URL_TTL_SECONDS)
    if (error) fail(error, 'storage')
    return data?.signedUrl ?? null
  },

  async markProofViewed(intakeEventId) {
    const supabase = requireSupabase()
    await requireUserId()
    const event = await fetchIntakeEventOrThrow(intakeEventId)
    if (!event.proofObjectPath) {
      fail(validationError('PROOF_MISSING', 'Er is nog geen bewijsvideo geüpload.'))
    }
    const { data, error } = await supabase
      .from('medication_intake_events')
      .update({ caregiver_viewed_at: new Date().toISOString(), status: 'proof_viewed' })
      .eq('id', intakeEventId)
      .select()
      .single()
    const updated = medicationIntakeEventFromRow(assertRowReturned(data, error) as MedicationIntakeEventRow)
    await updateOccurrenceStatus(event.occurrenceId, 'proof_viewed')
    return updated
  },

  async listIntakeEvents(clientId, limit = 50) {
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase
      .from('medication_intake_events')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) fail(error)
    return ((data ?? []) as MedicationIntakeEventRow[]).map(medicationIntakeEventFromRow)
  },

  async addCaregiverNote(occurrenceId, note) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const trimmed = note.trim()
    if (!trimmed) {
      fail(validationError('NOTE_EMPTY', 'Vul een notitie in.'))
    }
    const occurrence = await fetchOccurrenceOrThrow(occurrenceId)
    // Distinct caregiver audit action — safe_metadata carries the caregiver's
    // own note only (never journal text, URLs or tokens).
    const { data, error } = await supabase
      .from('audit_events')
      .insert({
        actor_id: userId,
        subject_user_id: occurrence.clientId,
        action: 'caregiver_note',
        entity_type: 'medication_occurrence',
        entity_id: occurrenceId,
        safe_metadata: { note: trimmed },
      })
      .select()
      .single()
    return auditEventFromRow(assertRowReturned(data, error) as AuditEventRow)
  },
}
